import { useEffect, useMemo, useState } from 'react';
import { api, UnauthorizedError, type AuditEntry } from '../api';

type Props = {
  onClose: () => void;
  onSessionExpired: () => void;
};

const PAGE = 100;

export function AuditLog({ onClose, onSessionExpired }: Props) {
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actions, setActions] = useState<string[]>([]);

  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [exhausted, setExhausted] = useState(false);

  const load = (reset: boolean) => {
    setLoading(true);
    setError(null);
    const before = reset ? undefined : rows[rows.length - 1]?.ts;
    api
      .audit({
        limit: PAGE,
        actor: actor.trim() || undefined,
        action: action || undefined,
        q: q.trim() || undefined,
        before,
      })
      .then((r) => {
        setRows((prev) => (reset ? r.data : [...prev, ...r.data]));
        setExhausted(r.data.length < PAGE);
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) onSessionExpired();
        else setError(String(e.message || e));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(true);
    api
      .auditActions()
      .then((r) => setActions(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilter = (e: React.FormEvent) => {
    e.preventDefault();
    load(true);
  };

  const onClear = () => {
    setActor('');
    setAction('');
    setQ('');
    setTimeout(() => load(true), 0);
  };

  const onExport = () => {
    const headers = [
      'ts',
      'iso',
      'actor_email',
      'actor_kind',
      'actor_id',
      'action',
      'resource',
      'status',
      'method',
      'path',
      'ip',
      'user_agent',
      'details',
    ];
    const csv = [headers.join(',')]
      .concat(
        rows.map((r) =>
          headers
            .map((h) => {
              if (h === 'iso') return JSON.stringify(new Date(r.ts * 1000).toISOString());
              const v = (r as any)[h];
              if (v === null || v === undefined) return '';
              return JSON.stringify(String(v));
            })
            .join(',')
        )
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spire-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const empty = !loading && rows.length === 0;

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="audit-modal">
      <div
        className="modal"
        style={{ maxWidth: 1080, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-head">
          <div className="modal-title">Auditoria</div>
          <button className="btn" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <p className="modal-sub">
          Log append-only de todas as ações autenticadas e dos eventos de auth públicos
          (tentativas de login, callbacks com falha). Cada linha registra ator, IP,
          user-agent e payload. Mostrando até <code>{PAGE}</code> por página.
        </p>

        <form className="key-form" onSubmit={onFilter} style={{ flexWrap: 'wrap', gap: 8 }}>
          <input
            type="search"
            placeholder="busca livre (ação, recurso, e-mail, detalhes)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="audit-q"
            style={{ minWidth: 220 }}
          />
          <input
            type="email"
            placeholder="filtrar por ator (email)"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            data-testid="audit-actor"
            style={{ minWidth: 200, flex: '0 1 240px' }}
          />
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            data-testid="audit-action"
          >
            <option value="">(todas as ações)</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <button className="btn primary" type="submit" data-testid="audit-filter">
            Filtrar
          </button>
          <button className="btn" type="button" onClick={onClear} data-testid="audit-clear">
            Limpar
          </button>
          <button
            className="btn"
            type="button"
            onClick={onExport}
            disabled={rows.length === 0}
            data-testid="audit-export"
          >
            Exportar CSV
          </button>
        </form>

        {error && (
          <div className="error" data-testid="audit-error">
            {error}
          </div>
        )}

        <div
          className="audit-list"
          data-testid="audit-list"
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {loading && rows.length === 0 && <div className="muted">carregando…</div>}
          {empty && <div className="muted">Nenhum registro encontrado para esses filtros.</div>}
          {rows.map((r) => (
            <AuditRowView
              key={r.id}
              row={r}
              expanded={expandedId === r.id}
              onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
            />
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          {!exhausted && rows.length > 0 && (
            <button
              className="btn"
              onClick={() => load(false)}
              disabled={loading}
              data-testid="audit-more"
            >
              {loading ? 'carregando…' : 'Carregar mais'}
            </button>
          )}
          {exhausted && rows.length > 0 && <span className="muted">fim da lista.</span>}
        </div>
      </div>
    </div>
  );
}

type RowProps = { row: AuditEntry; expanded: boolean; onToggle: () => void };

function AuditRowView({ row, expanded, onToggle }: RowProps) {
  const prettyDetails = useMemo(() => {
    if (!row.details) return null;
    try {
      return JSON.stringify(JSON.parse(row.details), null, 2);
    } catch {
      return row.details;
    }
  }, [row.details]);
  const statusColor =
    row.status == null
      ? 'var(--muted)'
      : row.status >= 500
        ? '#b91c1c'
        : row.status >= 400
          ? '#b45309'
          : 'var(--deep)';
  return (
    <div
      className="key-row audit-row"
      onClick={onToggle}
      style={{ cursor: 'pointer', alignItems: 'flex-start' }}
      data-testid={`audit-row-${row.id}`}
    >
      <div className="key-meta" style={{ flex: 1, minWidth: 0 }}>
        <div className="key-name" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <code style={{ background: 'var(--warm)', padding: '1px 6px', borderRadius: 4 }}>
            {row.action}
          </code>
          <span style={{ color: statusColor, fontWeight: 600, fontSize: 12 }}>
            {row.status ?? '—'}
          </span>
          {row.resource && (
            <span className="key-sub" style={{ fontSize: 12 }}>
              · <code>{row.resource}</code>
            </span>
          )}
        </div>
        <div
          className="key-sub"
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}
        >
          <span>{formatTs(row.ts)}</span>
          <span>·</span>
          <span>
            {row.actor_email || <em>anônimo</em>}
            <span style={{ color: 'var(--muted)', marginLeft: 4 }}>({row.actor_kind})</span>
          </span>
          {row.method && row.path && (
            <>
              <span>·</span>
              <span>
                <code>
                  {row.method} {row.path}
                </code>
              </span>
            </>
          )}
          {row.ip && (
            <>
              <span>·</span>
              <span>
                <code>{row.ip}</code>
              </span>
            </>
          )}
        </div>
        {expanded && (
          <div
            style={{
              marginTop: 8,
              padding: 10,
              border: '1px solid var(--line)',
              borderRadius: 6,
              background: 'var(--warm)',
              fontSize: 12,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {prettyDetails ? prettyDetails : <em>sem detalhes</em>}
            {row.user_agent && (
              <div style={{ marginTop: 8, color: 'var(--muted)' }}>
                <strong>UA:</strong> {row.user_agent}
              </div>
            )}
            {row.actor_id && (
              <div style={{ marginTop: 4, color: 'var(--muted)' }}>
                <strong>actor_id:</strong> {row.actor_id}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatTs(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
}
